// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { Scheduler } from "@aldabil/react-scheduler";
import { ProgressSpinner } from "primereact/progressspinner";
import Cookies from "js-cookie";
import type {
  ProcessedEvent,
  SchedulerHelpers,
} from "@aldabil/react-scheduler/types";
import "./admin_scheduler.css";
import Api from "../../api";
import Modal from "../Modal";
import Select from "../Select";
import CheckInput from "../CheckInput";
import {
  LANGUAGES,
  CLASS_TYPES,
  CAPACITYS,
} from "../../consts";
import {
  showNotification,
  getFormattedCurDate,
  getFormattedCurDateAdmin,
  hasUserRole,
} from "../../utils";
import Switch from "react-switch";
import moment from "moment";

interface CustomEditorProps {
  scheduler: SchedulerHelpers;
  getEvents: Function;
}

const dateOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
};

const CustomEditor = ({ scheduler }: CustomEditorProps) => {
  const startDateTime = scheduler.state.start.value;
  const startDateQueryParam = `start_date=${getFormattedCurDateAdmin(
    startDateTime
  )}`;

  const endDateTime = scheduler.state.end.value;
  const endDateQueryParam = `end_date=${getFormattedCurDateAdmin(endDateTime)}`;

  const defaultEvent = {
    teacher_id: "",
    start_date: getFormattedCurDateAdmin(startDateTime),
    end_date: getFormattedCurDateAdmin(endDateTime),
    zoom_key: "",
    location_id: "",
    type: "",
    sub_type: "",
    capacity: "",
    language: "",
    books: [],
    students: [],
  };

  const [loading, setLoading] = useState(false); // Estado de carga
  const [events, setEvents] = useState([defaultEvent]);
  const [teacherOptionsEnglish, setTeacherOptionsEnglish] = useState([]);
  const [teacherOptionsFrench, setTeacherOptionsFrench] = useState([]);
  const [booksOptionsEnglish, setBooksOptionsEnglish] = useState([]);
  const [booksOptionsFrench, setBooksOptionsFrench] = useState([]);
  const [zoomKeyOptions, setZoomKeyOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [addStudents, setAddStudents] = useState(false);
  const [subtypes, setSubtypes] = useState([]);
  const [subtypesLoaded, setSubtypesLoaded] = useState(false);
  const textareaRefs = useRef([]); // Manejo de referencias dinámicas para textarea
  const booksRefs = useRef([]); // Referencias para cada select de libros

  useEffect(() => {
    fetchLocationData();
    fetchZoomData();
    fetchTeachersAndBooks();
  }, []);

  const fetchLocationData = async () => {
    try {
      const data = await Api.Location.GetByFilters(``);
      setLocationOptions(
        data.map((l: any) => ({ value: `${l.id}`, label: l.address }))
      );
      /*showNotification("Éxito", "Sedes obtenidas correctamente", "success");*/
    } catch (error) {
      console.log(error);
      showNotification("Error", "Sedes no obtenidas", "danger");
    }
  };

  const fetchZoomData = async () => {
    try {
      const data = await Api.Zoom.GetByFilters(
        `?${startDateQueryParam}&${endDateQueryParam}`
      );
      setZoomKeyOptions(data.map((z: any) => ({ value: z.key, label: z.key })));
      /*showNotification(
        "Éxito",
        "Links de zoom obtenidos correctamente",
        "success"
      );*/
    } catch (error) {
      console.log(error);
      showNotification("Error", "Links de zoom no obtenidos", "danger");
    }
  };

  const fetchTeachersAndBooks = async () => {
    try {
      setLoading(true); // Iniciar carga
      const teachersEnglish = await Api.Teacher.GetByFilters(
        `?language=EN&${startDateQueryParam}`
      );
      setTeacherOptionsEnglish(
        teachersEnglish.map((t) => ({
          value: t.customer_id,
          label: t.full_name,
        }))
      );

      const teachersFrench = await Api.Teacher.GetByFilters(
        `?language=FR&${startDateQueryParam}`
      );
      setTeacherOptionsFrench(
        teachersFrench.map((t) => ({
          value: t.customer_id,
          label: t.full_name,
        }))
      );

      const booksEnglish = await Api.Book.GetByFilters("?language=EN");
      setBooksOptionsEnglish(
        booksEnglish.map((b) => ({ value: `${b.id}`, label: b.name }))
      );

      const booksFrench = await Api.Book.GetByFilters("?language=FR");
      setBooksOptionsFrench(
        booksFrench.map((b) => ({ value: `${b.id}`, label: b.name }))
      );
    } catch (error) {
      showNotification("Error", "No se pudieron cargar los datos", "danger");
    } finally {
      setLoading(false); // Finalizar carga
    }
  };

  const handleAddEvent = () => {
    if (events.length < 5) {
      setEvents([...events, { ...defaultEvent }]);
    } else {
      showNotification(
        "Error",
        "Solo puedes agregar hasta 5 eventos",
        "warning"
      );
    }
  };

  const handleRemoveEvent = (index) => {
    if (events.length > 1) {
      setEvents(events.filter((_, i) => i !== index));
    } else {
      showNotification("Error", "Debe haber al menos un evento", "warning");
    }
  };

  const loadSubtypes = async () => {
    const result = await Api.Subtype.List();
    if (!result?.ok || !Array.isArray(result.data)) return;
    setSubtypes(result.data);
    setSubtypesLoaded(true);
  };

  const subtypeOptionsFor = (language) =>
    subtypes
      .filter(
        (item) =>
          String(item.language || "").toUpperCase() ===
          String(language || "").toUpperCase()
      )
      .map((item) => ({
        value: item.subtype,
        label: item.subtype,
      }));

  useEffect(() => {
    loadSubtypes();
  }, []);

  const handleTextareaChange = (e, index) => {
    const updatedEvents = [...events];
    updatedEvents[index].students = e.target.value;
    setEvents(updatedEvents);

    // Mantener el foco al final del texto
    setTimeout(() => {
      if (textareaRefs.current[index]) {
        const textarea = textareaRefs.current[index];
        textarea.focus();
        textarea.setSelectionRange(
          textarea.value.length,
          textarea.value.length
        );
      }
      if (textareaRefs.current[index]) {
        textareaRefs.current[index].focus();
      }
    }, 0);
  };

  const handleSubmit = async () => {
    try {
      scheduler.loading(true);

      // Validar unicidad de profesores y Zoom keys
      const usedTeachers = new Set();
      const usedZoomKeys = new Set();

      for (const [i, event] of events.entries()) {
        const eventLabel = `evento #${i + 1}`;

        // Idioma obligatorio
        if (!event.language) {
          showNotification(
            "Error",
            `Selecciona un idioma en ${eventLabel}`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Tipo obligatorio
        if (!event.type) {
          showNotification(
            "Error",
            `Selecciona un tipo en ${eventLabel}`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Subtipo obligatorio si es WORKSHOP
        if (event.type === "WORKSHOP" && !event.sub_type) {
          showNotification(
            "Error",
            `El sub-tipo es obligatorio para talleres (${eventLabel})`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Profesor obligatorio
        if (!event.teacher_id) {
          showNotification(
            "Error",
            `Selecciona un profesor en ${eventLabel}`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Debe seleccionar sede o clave Zoom (una al menos)
        if (!event.location_id && !event.zoom_key) {
          showNotification(
            "Error",
            `Selecciona una sede o una clave de Zoom en ${eventLabel}`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Validar unicidad de profesor
        if (usedTeachers.has(event.teacher_id)) {
          showNotification(
            "Error",
            `Un profesor no puede repetirse en múltiples eventos`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Validar unicidad de Zoom Key
        if (event.zoom_key && usedZoomKeys.has(event.zoom_key)) {
          showNotification(
            "Error",
            `Una clave de Zoom no puede repetirse en múltiples eventos`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Capacidad obligatoria
        if (!event.capacity) {
          showNotification(
            "Error",
            `Selecciona una capacidad en ${eventLabel}`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Libros obligatorio
        if (!Array.isArray(event.books) || event.books.length === 0) {
          showNotification(
            "Error",
            `Debes seleccionar al menos un libro en ${eventLabel}`,
            "danger"
          );
          scheduler.loading(false);
          return;
        }

        // Registrar profesor y clave para validar unicidad
        usedTeachers.add(event.teacher_id);
        if (event.zoom_key) {
          usedZoomKeys.add(event.zoom_key);
        }
      }

      // Preparar el arreglo de eventos para enviar
      const payload = events.map((event) => ({
        ...event,
        books: Array.isArray(event.books)
          ? event.books.map((b) => b.value)
          : [],
        students:
          typeof event.students === "string"
            ? event.students.split(",").map((s) => s.trim())
            : [],
      }));

      // Enviar la lista de eventos
      const response = await Api.Class.Create(payload);

      // Manejo de la respuesta
      const createdEvents = Array.isArray(response) ? response : [response];

      createdEvents.forEach((createdEvent) => {
        scheduler.onConfirm(
          {
            id: createdEvent.event_id,
            title: createdEvent.type,
            start: startDateTime,
            end: endDateTime,
            teacher_name:
              createdEvent.language === "EN"
                ? teacherOptionsEnglish.find(
                    (t) => t.value === createdEvent.teacher_id
                  )?.label
                : teacherOptionsFrench.find(
                    (t) => t.value === createdEvent.teacher_id
                  )?.label,
            location_name: locationOptions.find(
              (l) => l.value === createdEvent.location_id
            )?.label,
            books: (createdEvent.books || []).map(
              (b) =>
                (createdEvent.language === "EN"
                  ? booksOptionsEnglish
                  : booksOptionsFrench
                ).find((book) => book.value === b.value)?.label
            ),
            capacity: createdEvent.capacity,
          },
          "create"
        );
      });

      showNotification("Éxito", "Eventos creados correctamente", "success");
      scheduler.close();
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      showNotification("Error", error.message, "danger");
    } finally {
      scheduler.loading(false);
    }
  };

  const modalContentForm = (
    <>
        {loading && (
          <>
            <div className="loading-overlay"></div>
            <div className="loading-spinner">
              <ProgressSpinner />
            </div>
          </>
        )}
        <div className="pair-container_admin">
          <div className="customer-id_container">
            <p>Fecha de inicio</p>
            {startDateTime.toLocaleString("es-CO", {
              dateStyle: "long",
              timeStyle: "short",
            })}
          </div>

          <div className="customer_id_type">
            <p>Fecha de finalización</p>
            {endDateTime.toLocaleString("es-CO", {
              dateStyle: "long",
              timeStyle: "short",
            })}
          </div>
        </div>

        {events.map((event, index) => (
          <div className="pair-container_admin" key={index}>
            <span className="number_event">{index + 1}</span>
            <div className="customer-id_container_admin">
              <p>Idioma</p>
              <Select
                name={`language-${index}`}
                options={LANGUAGES}
                value={LANGUAGES.find((l) => l.value === event.language)}
                onChange={(selected) => {
                  const updatedEvents = [...events];
                  updatedEvents[index].language = selected.value;
                  updatedEvents[index].teacher_id = "";
                  updatedEvents[index].books = [];
                  updatedEvents[index].sub_type = "";
                  setEvents(updatedEvents);
                }}
                placeholder="Idioma"
              />
            </div>

            <div className="customer-id_container_admin">
              <p>Tipo</p>
              <Select
                name={`type-${index}`}
                options={CLASS_TYPES}
                value={CLASS_TYPES.find((type) => type.value === event.type)}
                onChange={(selected) => {
                  const updatedEvents = [...events];
                  updatedEvents[index].type = selected.value;
                  setEvents(updatedEvents);
                  console.log(updatedEvents);
                }}
                placeholder="Tipo"
              />
            </div>

            <div className="customer-id_container_admin">
              <div className="create-students-container">
                <p>Sub-tipo</p>
                <CheckInput
                  isToggled={!!event.sub_type}
                  onToggle={() => {
                    const updatedEvents = [...events];
                    updatedEvents[index].sub_type = updatedEvents[index]
                      .sub_type
                      ? ""
                      : "default";
                    setEvents(updatedEvents);
                  }}
                />
              </div>

              <Select
                name={`sub_type-${index}`}
                options={subtypeOptionsFor(event.language)}
                value={subtypeOptionsFor(event.language).find(
                  (subType) => subType.value === event.sub_type
                )}
                onChange={(selected) => {
                  const updatedEvents = [...events];
                  updatedEvents[index].sub_type = selected.value;
                  setEvents(updatedEvents);
                }}
                disabled={!event.sub_type}
                placeholder={
                  event.language &&
                  subtypesLoaded &&
                  subtypeOptionsFor(event.language).length === 0
                    ? "Sin subtipos"
                    : "Sub-tipo"
                }
              />
            </div>

            <div className="customer-id_container_admin">
              <p>Profesor</p>
              <Select
                name={`teacher-${index}`}
                options={
                  event.language === "EN"
                    ? teacherOptionsEnglish
                    : event.language === "FR"
                    ? teacherOptionsFrench
                    : []
                }
                value={
                  event.language === "EN"
                    ? teacherOptionsEnglish.find(
                        (t) => t.value === event.teacher_id
                      )
                    : teacherOptionsFrench.find(
                        (t) => t.value === event.teacher_id
                      )
                }
                onChange={(selected) => {
                  const updatedEvents = [...events];
                  updatedEvents[index].teacher_id = selected.value;
                  setEvents(updatedEvents);
                  console.log(updatedEvents);
                }}
                placeholder="Profesor"
                isDisabled={!event.language}
              />
            </div>

            <div className="customer-id_container_admin">
              <p style={{ display: "flex" }}>
                Libros
                <span
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    display: "inline-block",
                  }}
                  onMouseEnter={() => {
                    if (booksRefs.current[index]) {
                      booksRefs.current[index].style.visibility = "visible";
                    }
                  }}
                  onMouseLeave={() => {
                    if (booksRefs.current[index]) {
                      booksRefs.current[index].style.visibility = "hidden";
                    }
                  }}
                  tabIndex={0}
                  aria-label="Ver libros seleccionados"
                >
                  <i
                    className="fa fa-eye fa-eye_class"
                    style={{ color: "#888", fontSize: 16 }}
                  ></i>
                  {/* Tooltip personalizado al lado derecho del icono */}
                  <div
                    ref={(el) => (booksRefs.current[index] = el)}
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "120%",
                      transform: "translateY(-110%)",
                      zIndex: 100,
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: 6,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      padding: "8px 12px",
                      minWidth: 180,
                      fontSize: 13,
                      color: "#333",
                      fontStyle: "italic",
                      visibility: "hidden",
                      transition: "visibility 0.2s",
                      maxHeight: 120,
                      overflowY: "auto",
                      whiteSpace: "normal",
                    }}
                  >
                    {event.books && event.books.length > 0 ? (
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        {event.books.map((book, i) => {
                          const opt = (
                            event.language === "EN"
                              ? booksOptionsEnglish
                              : booksOptionsFrench
                          ).find((b) => b.value === book.value);
                          return (
                            <li key={i} style={{ marginBottom: 2 }}>
                              {opt ? opt.label : book.label}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <span style={{ color: "#888" }}>
                        No hay libros seleccionados
                      </span>
                    )}
                  </div>
                </span>
              </p>
              <div style={{ flex: 1 }}>
                <Select
                  isMulti
                  className="books-select"
                  name={`books-${index}`}
                  options={
                    event.language === "EN"
                      ? booksOptionsEnglish
                      : event.language === "FR"
                      ? booksOptionsFrench
                      : []
                  }
                  value={(event.language === "EN"
                    ? booksOptionsEnglish
                    : booksOptionsFrench
                  ).filter((b) =>
                    event.books.some((book) => book.value === b.value)
                  )}
                  onChange={(selected) => {
                    const updatedEvents = [...events];
                    updatedEvents[index].books = selected;
                    setEvents(updatedEvents);
                  }}
                  placeholder="Libro(s)"
                  isDisabled={!event.language}
                  required={true}
                />
              </div>
            </div>

            <div className="customer-id_container_admin">
              <p>Sede</p>
              <Select
                name={`location-${index}`}
                options={locationOptions}
                value={locationOptions.find(
                  (l) => l.value === event.location_id
                )}
                onChange={(selected) => {
                  const updatedEvents = [...events];
                  updatedEvents[index].location_id = selected.value;
                  updatedEvents[index].zoom_key = ""; // Clear Zoom Key
                  setEvents(updatedEvents);
                  console.log(updatedEvents);
                }}
                placeholder="Sede"
              />
            </div>

            <div className="customer-id_container_admin">
              <p>Clave de Zoom</p>
              <Select
                name={`zoom-${index}`}
                options={zoomKeyOptions}
                value={zoomKeyOptions.find((z) => z.value === event.zoom_key)}
                onChange={(selected) => {
                  const updatedEvents = [...events];
                  updatedEvents[index].zoom_key = selected.value;
                  updatedEvents[index].location_id = ""; // Clear Location
                  setEvents(updatedEvents);
                  console.log(updatedEvents);
                }}
                placeholder="Zoom"
              />
            </div>

            <div className="customer-id_container_admin">
              <p>Capacidad</p>
              <Select
                name={`capacity-${index}`}
                options={CAPACITYS}
                value={CAPACITYS.find(
                  (capacity) => capacity.value === event.capacity
                )}
                onChange={(selected) => {
                  const updatedEvents = [...events];
                  updatedEvents[index].capacity = selected.value;
                  setEvents(updatedEvents);
                  console.log(updatedEvents);
                }}
                placeholder="Capacidad"
              />
            </div>

            <div className="customer-id_container_admin add-students">
              <div className="create-students-container">
                <span>Estudiantes</span>
                <CheckInput
                  isToggled={event.addStudents}
                  onToggle={() => {
                    const updatedEvents = [...events];
                    updatedEvents[index].addStudents =
                      !updatedEvents[index].addStudents;
                    setEvents(updatedEvents);
                  }}
                />
              </div>
              <textarea
                id={`students-${index}`}
                value={event.students}
                ref={(el) => (textareaRefs.current[index] = el)}
                disabled={!event.addStudents}
                onChange={(e) => handleTextareaChange(e, index)}
                placeholder="ID de estudiantes separados por coma"
              ></textarea>
            </div>

            {events.length > 1 && (
              <button
                className="handleRemoveEvent_Button"
                onClick={() => handleRemoveEvent(index)}
              >
                Eliminar
              </button>
            )}
          </div>
        ))}
        <button className="handleAddEvent_Button" onClick={handleAddEvent}>
          Agregar Evento
        </button>
    </>
  );

  return (
    <Modal
      isOpen={true}
      title="Crear sesión"
      Content={modalContentForm}
      onCancel={scheduler.close}
      onSubmit={handleSubmit}
      center={false}
      extraClasses="admin-scheduler-modal"
      renderBlurBackground={true}
    />
  );
};

export const CustomViewer = ({ event, close }) => {
  const [assistance, setAssistance] = useState([]);
  const [loading, setLoading] = useState(false); // Estado de carga
  const [showPassword, setShowPassword] = useState(false);
  const [teacherOptions, setTeacherOptions] = useState([]);

  const getAssistance = async () => {
    try {
      const data = await Api.Class.GetAssistance(event.id);
      setAssistance(data);
      console.log(data);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchTeacherData = async () => {
    try {
      const startDateQueryParam = `start_date=${getFormattedCurDateAdmin(
        event.start
      )}`;
      const endDateQueryParam = `end_date=${getFormattedCurDateAdmin(
        event.end
      )}`;

      const data = await Api.Teacher.GetByFilters(
        `?language=${event.language}&${startDateQueryParam}&${endDateQueryParam}`
      );

      setTeacherOptions(
        data.map((t: any) => ({ value: t.customer_id, label: t.full_name }))
      );

      showNotification(
        "Éxito",
        "Profesores obtenidos correctamente",
        "success"
      );
    } catch (error) {
      console.log(error);
      showNotification("Error", "Profesores no obtenidos", "danger");
    }
  };

  useEffect(() => {
    if (!hasUserRole(["ROLE_STUDENT"])) {
      getAssistance();
    }
  }, [event.id]);

  useEffect(() => {
    if (!hasUserRole(["ROLE_STUDENT"])) {
      fetchTeacherData();
    }
  }, [event.id, event.language]); // Dependencias que pueden cambiar la lista de profesores

  const ModalContentView = () => {
    const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null); // Estado para almacenar el profesor seleccionado

    const handleSwitchChange = (checked: boolean) => {
      setIsButtonEnabled(checked);
    };

    // Lógica para manejar el cambio de selección del profesor
    const handleTeacherChange = (selectedOption) => {
      setSelectedTeacher(selectedOption);
    };

    const handleUpdateTeacher = async () => {
      try {
        if (!selectedTeacher) {
          return; // No se ha seleccionado un profesor
        }

        const updateData = {
          teacher_id: selectedTeacher.value,
          start_date: event.start.toISOString(), // Utilizar la fecha y hora del evento
          end_date: event.end.toISOString(), // Utilizar la fecha y hora del evento
          type: event.type,
          language: event.language,
          books: event.books.map((book) => book.value), // Obtener solo los valores de los libros
        };

        const updateUrl = `${process.env.REACT_APP_API_URL}/api/v1/${event.id}/teacher`;
        const updateResponse = await fetch(updateUrl, {
          method: "PUT", // Asumiendo que el método correcto es PUT para actualizar
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: JSON.stringify(updateData),
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          throw new Error(
            errorData.message || "No se pudo actualizar el profesor"
          );
        }

        showNotification(
          "Éxito",
          "Profesor actualizado correctamente",
          "success"
        );
      } catch (error) {
        console.error(error);
        showNotification("Error", error.message, "danger");
      } finally {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    };

    const handleButtonDelete = async () => {
      try {
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/class/${event.id}`;
        const response = await fetch(apiUrl, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || "No se pudo eliminar la clase");
        }

        showNotification("Éxito", "Clase eliminada correctamente", "success");
        close(); // Cierra el modal después de la eliminación exitosa
      } catch (error) {
        console.error(error);
        showNotification("Error", error.message, "danger");
      } finally {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    };

    return (
      <>
        <div className="pair-container">
          <div className="customer-id_container">
            <p>Fecha de inicio</p>
            {event.start.toLocaleString("es-CO", { dateOptions })}
          </div>

          <div className="customer_id_type">
            <p>Fecha de finalización</p>
            {event.end.toLocaleString("es-CO", { dateOptions })}
          </div>
        </div>

        <div className="pair-container">
          <div className="customer-id_container">
            <p>Idioma</p>
            {LANGUAGES.find((dc) => dc.value === event.language).label}
          </div>

          <div className="customer_id_type">
            <div className="events_types">
              <div className="events_types_1">
                <p>Tipo:</p>
                {event.type}
              </div>
              <div>
                <p>Sub-tipo:</p>
                {event.sub_type}
              </div>
            </div>
          </div>
        </div>

        <div className="pair-container">
          <div className="customer-id_container">
            <p>Capacidad</p>
            {event.capacity}
          </div>
          {/* Selector de Profesores */}
          {!hasUserRole(["ROLE_STUDENT"]) && (
            <div className="teacher-container">
              <div className="change-teacher_container">
                <div className="teacher-id_container">
                  <p>Profesor</p>
                  Profesor actual: {event.teacher_name}
                  <div className="action_teacher_container">
                    <Select
                      name="teacher"
                      options={teacherOptions}
                      className="teacher_avalaible-select"
                      value={selectedTeacher}
                      onChange={handleTeacherChange}
                      isDisabled={
                        !teacherOptions || teacherOptions.length === 0 // Deshabilitar si no hay opciones de profesores disponibles
                      }
                    />
                    <button
                      className="button-update_teacher"
                      type="button"
                      onClick={handleUpdateTeacher}
                      disabled={!selectedTeacher}
                    >
                      Actualizar Profesor
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pair-container">
          <div className="customer-id_container">
            <p>Ubicación</p>
            {event.location_name}
          </div>
        </div>

        {!hasUserRole(["ROLE_STUDENT"]) && event.zoom_email ? (
          <div className="pair-container">
            <div className="customer-id_container">
              <p>Correo</p>
              {event.zoom_email || "-"}
            </div>

            <div className="customer_id_type">
              <p>Contraseña</p>
              <div style={{ position: "relative" }}>
                <input
                  className="register_form-password_input"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={event.zoom_password}
                  onChange={(e) => {}}
                  disabled={true}
                />
                <i
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                  className="fa fa-eye"
                  id="eye"
                ></i>
              </div>
            </div>
          </div>
        ) : (
          ""
        )}

        <div className="customer_id_type full-width">
          <p>Libros</p>
          <div className="pace_info-tables">
            <table className="basic_info-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                </tr>
              </thead>
              <tbody>
                {event.books.map((book_name, index) => (
                  <tr key={index}>
                    <td>{book_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {!hasUserRole(["ROLE_STUDENT"]) ? (
          <div className="customer_id_type full-width">
            <p>Estudiantes</p>
            {assistance.length ? (
              <div className="pace_info-tables scroll-heigth-10-rem">
                <table className="basic_info-table">
                  <thead>
                    <tr>
                      <th>Id</th>
                      <th>Nombre</th>
                      <th>Libro</th>
                      <th>Sesión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assistance.map((student, index) => (
                      <tr key={index}>
                        <td>{student.customer_id}</td>
                        <td>{student.full_name}</td>
                        <td>{student.book_name}</td>
                        <td>{student.session_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              "No hay estudiantes inscritos."
            )}
          </div>
        ) : (
          ""
        )}
        <div className="delete-container">
          <div className="delete-container_label">
            Activar/Desactivar para eliminar clase
          </div>
          <div className="delete-container_button">
            <Switch
              className="switch-delete"
              checked={isButtonEnabled}
              onChange={handleSwitchChange}
            />

            <button
              className="button-delete_class"
              type="button"
              onClick={handleButtonDelete}
              disabled={!isButtonEnabled}
            >
              Eliminar Clase
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal
      isOpen={true}
      title="Detalle"
      Content={ModalContentView}
      onCancel={close}
      onSubmit={close}
      center={false}
      extraClasses="admin-scheduler-detail absolute-center"
      renderBlurBackground={false}
      showConfirm={false}
    />
  );
};

export const CustomViewerGeneral = ({ event, close }) => {
  const [assistance, setAssistance] = useState([]);
  const [loading, setLoading] = useState(false); // Estado de carga
  const [showPassword, setShowPassword] = useState(false);
  const [sessionInfo, setSessionInfo] = useState([]);
  const hideStudentId =
    hasUserRole(["ROLE_TEACHER"]) && !hasUserRole(["ROLE_ADMIN"]);

  const getAssistance = async () => {
    try {
      const data = await Api.Class.GetAssistance(event.id);
      setAssistance(data);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchTeacherData = async () => {
    try {
      const startDateQueryParam = `start_date=${getFormattedCurDateAdmin(
        event.start
      )}`;
      const endDateQueryParam = `end_date=${getFormattedCurDateAdmin(
        event.end
      )}`;

      const data = await Api.Teacher.GetByFilters(
        `?language=${event.language}&${startDateQueryParam}&${endDateQueryParam}`
      );

      setTeacherOptions(
        data.map((t: any) => ({ value: t.customer_id, label: t.full_name }))
      );

      showNotification(
        "Éxito",
        "Profesores obtenidos correctamente",
        "success"
      );
    } catch (error) {
      console.log(error);
    }
  };

  const fetchClassSession = async () => {
    if (event.type === "EXAM") {
      return; // No realizar el fetch si el tipo de evento es EXAM
    }

    try {
      const data = await Api.Class.GetSession(event.id);

      showNotification(
        "Éxito",
        "Información de la sesión obtenida correctamente",
        "success"
      );

      setSessionInfo(data); // Guardar los datos en el estado
    } catch (error) {
      console.error("Error obteniendo la sesión:", error);
      showNotification(
        "Error",
        "No se pudo obtener la información de la sesión",
        "danger"
      );
    }
  };

  useEffect(() => {
    if (!hasUserRole(["ROLE_STUDENT"])) {
      getAssistance();
    }
  }, [event.id]);

  useEffect(() => {
    if (!hasUserRole(["ROLE_STUDENT"])) {
      fetchTeacherData();
    }
  }, [event.id, event.language]); // Dependencias que pueden cambiar la lista de profesores

  useEffect(() => {
    if (!hasUserRole(["ROLE_TEACHER"])) {
      fetchClassSession(event.id);
    }
  }, [event.id]);

  const ModalContentView = () => {
    return (
      <>
        <div className="pair-container">
          <div className="customer-id_container">
            <p>Fecha de inicio</p>
            {event.start.toLocaleString("es-CO", { dateOptions })}
          </div>

          <div className="customer_id_type">
            <p>Fecha de finalización</p>
            {event.end.toLocaleString("es-CO", { dateOptions })}
          </div>
        </div>

        <div className="pair-container">
          <div className="customer-id_container">
            <p>Idioma</p>
            {LANGUAGES.find((dc) => dc.value === event.language).label}
          </div>

          <div className="customer_id_type">
            <div className="events_types">
              <div className="events_types_1">
                <p>Tipo:</p>
                {event.type}
                {event.type === "EXAM" && (
                  <>
                    {event.language === "EN" && (
                      <>
                        <h3>
                          <a
                            href="https://docs.google.com/forms/d/e/1FAIpQLSdQcqKIHAxlhrrLFyozudUM8E4SEfKpCkY4ecHM1MDnM5NXjw/viewform"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Forms Examen
                          </a>
                        </h3>
                      </>
                    )}
                    {event.language === "FR" && (
                      <>
                        <h3>
                          <a
                            href="https://docs.google.com/forms/d/e/1FAIpQLSfsDVhYwJPiGxOzIKWJdz5WhwaSo3XbWuGtOxltMvuNE_SJ8w/viewform"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Forms Examen
                          </a>
                        </h3>
                      </>
                    )}
                  </>
                )}
              </div>
              <div>
                <p>Sub-tipo:</p>
                {event.sub_type}
              </div>
            </div>
          </div>
        </div>

        <div className="pair-container">
          <div className="customer-id_container">
            <p>Capacidad</p>

            {event.capacity}
          </div>
        </div>

        <div className="pair-container">
          <div className="customer-id_container">
            <p>Ubicación</p>
            {(() => {
              // Si la ubicación es presencial, mostrar "Sede"
              if (!event.location_name.startsWith("S-")) {
                return (
                  <>
                    <p>Sede:</p>
                    <h3>{event.location_name || "No disponible"}</h3>
                  </>
                );
              }

              // Si la ubicación es virtual (Zoom), mantener el formato de Zoom
              const [salon, id] = event.location_name?.split(" - ") || [];
              const zoomId = id ? id.replace(/-/g, "") : null;
              const zoomLink = zoomId
                ? `https://us04web.zoom.us/j/${zoomId}`
                : null;

              return (
                <>
                  <p>Salón de Zoom:</p> <h3>{salon || "No disponible"}</h3>
                  <p>ID de Zoom:</p>
                  {zoomLink ? (
                    <h3>
                      <a
                        href={zoomLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {id}
                      </a>
                    </h3>
                  ) : (
                    <h3>No disponible</h3>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {!hasUserRole(["ROLE_STUDENT"]) && event.zoom_email ? (
          <div className="pair-container">
            <div className="customer-id_container">
              <p>Correo</p>
              {event.zoom_email || "-"}
            </div>

            <div className="customer_id_type">
              <p>Contraseña</p>
              <div style={{ position: "relative" }}>
                <input
                  className="register_form-password_input"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={event.zoom_password}
                  onChange={(e) => {}}
                  disabled={true}
                />
                <i
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                  className="fa fa-eye"
                  id="eye"
                ></i>
              </div>
            </div>
          </div>
        ) : (
          ""
        )}

        <div className="customer_id_type full-width">
          <p>Libros</p>
          <div className="pace_info-tables">
            <table className="basic_info-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                </tr>
              </thead>
              <tbody>
                {event.books.map((book_name, index) => (
                  <tr key={index}>
                    <td>{book_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasUserRole(["ROLE_STUDENT"]) && (
            <>
              {event.type === "EXAM" ? (
                <p>Examen programado para esta sesión</p>
              ) : (
                <>
                  <p>Tu información</p>
                  <div style={{ display: "flex" }}>
                    <p>
                      <strong>Libro:&nbsp;</strong>
                    </p>
                    <h3>
                      <u>{sessionInfo.book_name}</u>
                    </h3>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <strong>Sesión:&nbsp;</strong>
                    </p>
                    <h3>
                      <u>{sessionInfo.session_name}</u>
                    </h3>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {!hasUserRole(["ROLE_STUDENT"]) ? (
          <div className="customer_id_type full-width">
            <p>Estudiantes</p>
            {assistance.length ? (
              <div className="pace_info-tables scroll-heigth-10-rem">
                <table className="basic_info-table">
                  <thead>
                    <tr>
                      {!hideStudentId && <th>Id</th>}
                      <th>Nombre</th>
                      <th>Libro</th>
                      <th>Sesión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assistance.map((student, index) => (
                      <tr key={index}>
                        {!hideStudentId && <td>{student.customer_id}</td>}
                        <td>{student.full_name}</td>
                        <td>{student.book_name}</td>
                        <td>{student.session_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              "No hay estudiantes inscritos."
            )}
          </div>
        ) : (
          ""
        )}
      </>
    );
  };

  return (
    <Modal
      isOpen={true}
      title="Detalle"
      Content={ModalContentView}
      onCancel={close}
      onSubmit={close}
      center={false}
      extraClasses="admin-scheduler-detail absolute-center"
      renderBlurBackground={false}
      showConfirm={false}
    />
  );
};

const Admin_scheduler = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false); // Estado de carga
  const [customerId, setCustomerId] = useState(undefined);

  const getEvents = async (filters: any) => {
    try {
      const data = await Api.Class.GetByFilters(filters);
      setEvents(
        data.map((e: any) => {
          const newEvent = {
            ...e,
            event_id: e.id || Math.random(),
            title: e.type,
            start: new Date(e.start_date),
            end: new Date(e.end_date),
          } as ProcessedEvent;

          return newEvent;
        })
      );
      showNotification("Éxito", "Eventos obtenidos correctamente", "success");
    } catch (error) {
      console.log(error);
      showNotification("Error", "Eventos no obtenidos", "danger");
    }
  };

  const handleSearchUser = async () => {
    try {
      await getEvents(
        `?customer_id=${customerId}&start_date=${getFormattedCurDateAdmin(
          new Date()
        )}`
      );
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div>
      {loading && (
        <>
          <div className="loading-overlay"></div>
          <div className="loading-spinner">
            <ProgressSpinner />
          </div>
        </>
      )}
      <div className="selectedUserAdminScheduler selectedUser">
        <h4>Ingrese la identificacion del usuario</h4>
        <input
          className="customer_id"
          type="text"
          id="customer_id-search"
          placeholder="Identificacion"
          value={customerId}
          onChange={(e) =>
            //@ts-ignore
            setCustomerId(e.target.value)
          }
        />

        <div className="submit-buttons-container">
          <button
            className="button-search_user"
            type="button"
            onClick={handleSearchUser}
          >
            Buscar
          </button>
        </div>
      </div>

      <Scheduler
        events={events}
        month={{
          weekDays: [0, 1, 2, 3, 4, 5, 6],
          weekStartOn: 1,
          startHour: 6,
          endHour: 21,
        }}
        week={{
          weekDays: [0, 1, 2, 3, 4, 5, 6],
          weekStartOn: 1,
          startHour: 6,
          endHour: 21,
          step: 90,
        }}
        day={{
          startHour: 6,
          endHour: 21,
          step: 90,
        }}
        editable={true}
        deletable={true}
        customEditor={(scheduler) => {
          const start = scheduler.state.start.value;
          const rest =
            scheduler.state.end.value.getTime() -
            scheduler.state.start.value.getTime();
          const threshold = 1.5 * 60 * 60 * 1000;

          const showError = () => {
            showNotification(
              "Error",
              "La fecha seleccionada debe ser mayor o igual a la fecha actual",
              "danger"
            );
            scheduler.close();
          };

          const showErrorTwo = () => {
            showNotification(
              "Error",
              "No se puede crear una clase desde la vista de mes",
              "danger"
            );
            scheduler.close();
          };

          if (moment(start).isBefore(moment(new Date()))) {
            showError();
          } else if (rest > threshold) {
            showErrorTwo();
          } else {
            return <CustomEditor scheduler={scheduler} getEvents={getEvents} />;
          }
        }}
        customViewer={(event, close) => (
          <CustomViewer event={event} close={close} />
        )}
      />
    </div>
  );
};

export default Admin_scheduler;
